import React, { useCallback, useEffect, useState } from "react";
import { mergeRight } from "ramda";

import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContentText from "@mui/material/DialogContentText";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";

import DialogContent from "@mui/material/DialogContent";
import { lensProp, pick, set } from "ramda";
import Grid from "@mui/material/Grid";
import { FetchError } from "../util/fetchUtils";
import { useDispatch } from "react-redux";
import { logOut } from "../auth/authActions";
import MuiAlert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { notBlank } from "../util/ext";
import {
  useApplicationAccountCreate,
  useApplicationAccountUpdate,
} from "./applicationAccountServices";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const Alert = React.forwardRef((props, ref) => {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const initialState = {
  fields: {
    applicationName: "",
    accessRestricted: false,
  },
};

const EditApplicationAccount = ({ account, open, onClose, onSave }) => {
  const dispatch = useDispatch();
  const [fields, setFields] = useState(initialState.fields);
  const [errorAlert, setErrorAlert] = useState({ open: false });

  useEffect(() => {
    if (account && account.applicationId) {
      setFields(
        pick(["applicationName", "active", "accessRestricted"], account),
      );
    } else {
      setFields(initialState.fields);
    }
  }, [account]);

  const onFieldChanged = field => (event, optVal) => {
    const value = event.target.value || optVal;
    const update = set(lensProp(field), value, fields);
    setFields(update);
  };

  const showAlert = message =>
    setErrorAlert({
      open: true,
      message,
    });

  const accountUpdate = useApplicationAccountUpdate();
  const updateAccount = async acct => {
    console.log("Updating Application Account");
    await accountUpdate.mutateAsync({
      resourceIds: { id: acct.applicationId },
      data: acct,
    });
  };

  const accountCreate = useApplicationAccountCreate();
  const createAccount = async acct => {
    console.log("Creating Application Account");
    if (notBlank(acct.applicationName)) {
      await accountCreate.mutateAsync({ data: acct });
    } else {
      throw new Error("An Application Name is required.");
    }
  };

  const handleError = (message, err) => {
    if (err instanceof FetchError) {
      if (err.status === 401) {
        dispatch(logOut());
      } else {
        showAlert(message);
      }
    } else {
      showAlert(err.message);
    }
  };

  const handleOnSave = async () => {
    const mergedAccount = mergeRight(account, fields);

    try {
      if (notBlank(mergedAccount.applicationId)) {
        await updateAccount(mergedAccount);
      } else {
        await createAccount(mergedAccount);
      }

      onSave(mergedAccount);
    } catch (err) {
      handleError("Unable to save Application Account.", err);
    }
  };

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleOnClose}
      aria-labelledby="form-dialog-title"
    >
      {account.applicationId ? (
        <DialogTitle id="form-dialog-title">
          Application Account ({account.applicationId})
        </DialogTitle>
      ) : (
        <DialogTitle id="form-dialog-title">
          New Application Account
        </DialogTitle>
      )}
      <DialogContent>
        <DialogContentText>Enter Account Information.</DialogContentText>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              id="application"
              label="Application Account Name"
              fullWidth
              value={fields.applicationName || ""}
              onChange={onFieldChanged("applicationName")}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={fields.accessRestricted}
                    onChange={onFieldChanged("accessRestricted")}
                    name="accessRestricted"
                    color="primary"
                  />
                }
                label="Restricted"
              />
            </FormControl>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleOnClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleOnSave} color="primary">
          Save
        </Button>
      </DialogActions>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorAlert.open}
        autoHideDuration={6000}
        onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
      >
        <Alert
          onClose={() => setErrorAlert(mergeRight(errorAlert, { open: false }))}
          severity="error"
        >
          {errorAlert.message}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default EditApplicationAccount;
